/**
 * WebSocket типы для Frontend
 * Синхронизированы с Backend типами
 */

/**
 * Типы WebSocket событий
 */
export enum WebSocketEventType {
  // System Events
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
  HEARTBEAT = 'HEARTBEAT',
  ERROR = 'ERROR',
  AUTH = 'AUTH',

  // Data Sync Events
  DATA_UPDATED = 'DATA_UPDATED',
  DATA_CREATED = 'DATA_CREATED',
  DATA_DELETED = 'DATA_DELETED',
  
  // Subscription Management
  SUBSCRIBE = 'SUBSCRIBE',
  UNSUBSCRIBE = 'UNSUBSCRIBE',
  /** Отказ в подписке на канал — не фатален для соединения (в отличие от ERROR) */
  SUBSCRIPTION_DENIED = 'SUBSCRIPTION_DENIED',

  // Presence Events
  USER_JOINED_STAGE = 'USER_JOINED_STAGE',
  USER_LEFT_STAGE = 'USER_LEFT_STAGE',
  STAGE_PRESENCE = 'STAGE_PRESENCE',

  // System Maintenance
  MAINTENANCE_MODE = 'MAINTENANCE_MODE',
}

/**
 * Базовая структура WebSocket сообщения
 */
export interface WebSocketMessage<T = unknown> {
  type: WebSocketEventType;
  payload: T;
  timestamp: string;
  clientId?: string;
  userId?: string;
}

/**
 * Payload для DATA_UPDATED события
 */
export interface DataUpdatedPayload {
  queryKey: string[];
  tableId?: string;             // ID конкретной таблицы на фронтенде (например, 'price-planning-pack')
  entityType?: string;
  action?: 'update' | 'create' | 'delete';
  recordIds?: (string | number)[];
  userId?: string;
  userName?: string;
}

/**
 * Payload для CONNECTED события
 */
export interface ConnectedPayload {
  clientId: string;
  serverTime: string;
  message: string;
}

/**
 * Payload для подписки на каналы
 */
export interface SubscriptionPayload {
  channels: string[];
}

/**
 * Payload для SUBSCRIPTION_DENIED события
 */
export interface SubscriptionDeniedPayload {
  channel: string;
}

/**
 * Payload для AUTH события
 */
export interface AuthPayload {
  token: string;
}

/**
 * Payload для AUTH_SUCCESS ответа
 */
export interface AuthSuccessPayload {
  userId: string;
  userName: string;
  message: string;
}

/**
 * Payload для MAINTENANCE_MODE события
 */
export interface MaintenanceModePayload {
  enabled: boolean;
  message?: string;
}

/**
 * Статус WebSocket соединения
 */
export enum WebSocketStatus {
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
  RECONNECTING = 'RECONNECTING',
  ERROR = 'ERROR',
}

/**
 * Настройки WebSocket соединения
 */
export interface WebSocketConfig {
  url: string;
  token?: string;                  // Статичный токен (используется если getToken не задан)
  getToken?: () => string | null | Promise<string | null>;  // Свежий токен при каждом (пере)подключении
  reconnectInterval?: number;      // Интервал переподключения в мс (по умолчанию 3000)
  maxReconnectAttempts?: number;   // Максимальное количество попыток (по умолчанию 5)
  heartbeatInterval?: number;      // Интервал heartbeat в мс (по умолчанию 60000 - оптимизировано)
  autoConnect?: boolean;           // Автоматическое подключение (по умолчанию true)
}

/**
 * Один пользователь в presence-списке этапа
 */
export interface StagePresenceUser {
  userId: string;
  userName: string;
}

/**
 * Payload для STAGE_PRESENCE / USER_JOINED_STAGE / USER_LEFT_STAGE
 */
export interface StagePresencePayload {
  stage: string;
  users: StagePresenceUser[];
}

/**
 * Обработчик WebSocket событий
 */
export type WebSocketEventHandler<T = unknown> = (payload: T, message: WebSocketMessage<T>) => void;

/**
 * Карта обработчиков событий
 */
export type WebSocketEventHandlers = {
  [K in WebSocketEventType]?: WebSocketEventHandler;
};


